// FileName: gridPositioningUtils.ts - Utility functions for converting between grid and XY coordinate systems

import { XYPosition } from '@xyflow/react';
import { SWEEP_GRID_SIZE } from '../const';
import { PADDING_WIDTH } from '../nodes/helpers';
import { CanvasElementType } from '../canvas-types';

export interface CanvasGridPosition {
  row: number;
  column: number;
}

const defaultNodePadding = { x: PADDING_WIDTH, y: 0 } satisfies XYPosition;
const noNodePadding = { x: 0, y: 0 } satisfies XYPosition;

const nodeTypeAdditionalPaddingMap: Record<CanvasElementType, XYPosition> = {
  [CanvasElementType.REGULAR]: defaultNodePadding,
  [CanvasElementType.EDITABLE]: defaultNodePadding,
  [CanvasElementType.NURTURING_BUCKET]: defaultNodePadding,
  [CanvasElementType.DROP_ZONE_NODE]: defaultNodePadding,
  [CanvasElementType.GROUP]: { x: -PADDING_WIDTH, y: 0 },
  [CanvasElementType.GROUP_LABEL]: defaultNodePadding,
  [CanvasElementType.GROUP_OVERLAY]: defaultNodePadding,
  [CanvasElementType.GHOST_NODE]: defaultNodePadding,
  [CanvasElementType.STANDALONE_NODE]: noNodePadding,
  [CanvasElementType.SECTION]: noNodePadding,
  [CanvasElementType.STICKY_NOTE]: noNodePadding,
  [CanvasElementType.TEXT_NODE]: noNodePadding,
};

const getPadding = (nodeType?: CanvasElementType): XYPosition => {
  return nodeType ? nodeTypeAdditionalPaddingMap[nodeType] : noNodePadding;
};

export const convertXYPositionToGridPosition = (
  position: XYPosition,
  options?: { withPaddingByNodeType?: CanvasElementType },
): CanvasGridPosition => {
  const padding = getPadding(options?.withPaddingByNodeType);
  return {
    column: (position.x - padding.x) / SWEEP_GRID_SIZE.width,
    row: -(position.y - padding.y) / SWEEP_GRID_SIZE.height,
  };
};

export const snapPositionToGrid = (
  position: XYPosition,
  options?: { withPaddingByNodeType?: CanvasElementType },
): XYPosition => {
  const padding = getPadding(options?.withPaddingByNodeType);
  return {
    x: Math.round(position.x / SWEEP_GRID_SIZE.width) * SWEEP_GRID_SIZE.width + padding.x,
    y: Math.round(position.y / SWEEP_GRID_SIZE.height) * SWEEP_GRID_SIZE.height + padding.y,
  };
};

export const convertXYPositionToGridIndex = (
  position: XYPosition = { x: 0, y: 0 },
  options?: { withPaddingByNodeType?: CanvasElementType },
): CanvasGridPosition => {
  const padding = getPadding(options?.withPaddingByNodeType);
  return {
    column: Math.round((position.x - padding.x) / SWEEP_GRID_SIZE.width),
    row: Math.round(-(position.y - padding.y) / SWEEP_GRID_SIZE.height),
  };
};

export const convertGridPositionToXYPosition = (
  position: CanvasGridPosition = { column: 0, row: 0 },
  options?: { withPaddingByNodeType?: CanvasElementType },
): XYPosition => {
  const padding = getPadding(options?.withPaddingByNodeType);
  return {
    x: position.column * SWEEP_GRID_SIZE.width + padding.x,
    y: -(position.row * SWEEP_GRID_SIZE.height) + padding.y,
  };
};
